import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

export interface ButtonGroupProps extends ThemeOverrideProps {
  /**
   * Layout orientation of the buttons
   * @default 'horizontal'
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Force or suppress the split ("space-between") layout, in which the first
   * button is pinned to the left edge and the remaining buttons are grouped
   * on the right.
   *
   * When omitted, the layout is chosen by child count: horizontal groups with
   * 3 or more buttons split, smaller groups do not. Set `split` to override
   * that heuristic in either direction:
   *
   * - `split` — split a 2-button group (buttons keep their natural width
   *   instead of stretching to fill the row)
   * - `split={false}` — opt a 3+ button group out of the split layout
   *
   * Has no effect when `orientation="vertical"` or when the group has fewer
   * than 2 children.
   */
  split?: boolean;
  /**
   * Size of the button group, determines default gap between buttons
   * @default 'md'
   */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /**
   * Buttons to be grouped
   */
  children: React.ReactNode;
  /**
   * Optional description text below the buttons
   */
  description?: string;
  /**
   * Optional error message text below the buttons
   */
  error?: string;
  /**
   * Custom gap between buttons (in pixels). If not provided, uses size and orientation based default.
   */
  gap?: number;
  /**
   * Accessible label for the button group
   */
  "aria-label"?: string;
  /**
   * ID of element that labels this button group
   */
  "aria-labelledby"?: string;
  /**
   * ID of element that describes this button group
   */
  "aria-describedby"?: string;
  id?: string;
  testID?: string;
}

/**
 * ButtonGroup - A container for grouping related buttons
 *
 * Provides semantic grouping for related actions with proper accessibility support.
 *
 * ## Accessibility Features
 *
 * - **Semantic Grouping**: Uses `role="group"` to indicate related buttons
 * - **Accessible Name**: Supports `aria-label` or `aria-labelledby` to describe the group's purpose
 * - **Error Announcements**: Errors are announced to screen readers via `aria-live`
 * - **Description Support**: Optional description text for additional context
 *
 */
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  orientation = "horizontal",
  split,
  size = "md",
  children,
  description,
  error,
  gap,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  id,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  // Flatten children to handle fragments and get actual button elements
  const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === React.Fragment) {
        result.push(...flattenChildren(child.props.children));
      } else if (child !== null && child !== undefined) {
        result.push(child);
      }
    });
    return result;
  };

  const flatChildren = flattenChildren(children);
  const childCount = flatChildren.length;

  // `split` overrides the child-count heuristic in both directions. A single
  // child can never split, regardless of the prop.
  const useSpaceBetween =
    orientation === "horizontal" && childCount > 1 && (split ?? childCount > 2);

  // Size-based default gaps by orientation
  const verticalGapMap = {
    xl: 16,
    lg: 16,
    md: 12,
    sm: 8,
    xs: 4,
  };

  const horizontalGapMap = {
    xl: 16,
    lg: 16,
    md: 16,
    sm: 12,
    xs: 12,
  };

  const computedGap =
    gap ??
    (orientation === "vertical"
      ? verticalGapMap[size]
      : horizontalGapMap[size]);

  const descriptionId = id ? `${id}-description` : undefined;
  const errorId = id ? `${id}-error` : undefined;

  const computedAriaDescribedBy =
    [
      ariaDescribedBy,
      error && errorId ? errorId : undefined,
      description && descriptionId ? descriptionId : undefined,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  // Per Figma: vertical buttons always stretch; horizontal 1-2 buttons fill
  // the full row width (a pair splits 50/50); 3+ use space-between.
  // An explicit `split` opts a pair into space-between, and stretched children
  // would fill the row and hide that layout — so never stretch when splitting.
  const stretchChildren =
    orientation === "vertical" ||
    (orientation === "horizontal" && childCount <= 2 && !useSpaceBetween);

  const processChildren = (childrenToProcess: React.ReactNode[]) => {
    if (stretchChildren) {
      return childrenToProcess.map((child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            ...child.props,
            fullWidth: true,
            key: child.key ?? index,
          });
        }
        return child;
      });
    }
    return childrenToProcess;
  };

  // Split children for space-between layout
  const renderChildren = () => {
    const processedChildren = processChildren(flatChildren);

    if (useSpaceBetween) {
      const firstChild = processedChildren[0];
      const restChildren = processedChildren.slice(1);

      return (
        <>
          {firstChild}
          <Box flexDirection="row" gap={computedGap}>
            {restChildren}
          </Box>
        </>
      );
    }

    // For non-space-between layout, processChildren is a no-op unless
    // the children should stretch
    return processedChildren;
  };

  return (
    <Box flexDirection="column" width="100%" gap={8}>
      <Box
        role="group"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={computedAriaDescribedBy}
        id={id}
        testID={testID}
        flexDirection={orientation === "horizontal" ? "row" : "column"}
        alignItems="stretch"
        gap={computedGap}
        justifyContent={useSpaceBetween ? "space-between" : undefined}
        width="100%"
      >
        {renderChildren()}
      </Box>

      {error && (
        <Box marginTop={4}>
          <Text
            id={errorId}
            role="alert"
            aria-live="assertive"
            color={theme.colors.content.alert.primary}
            fontSize={14}
            fontWeight="400"
            style={
              orientation === "vertical" ? { textAlign: "center" } : undefined
            }
          >
            {error}
          </Text>
        </Box>
      )}

      {description && (
        <Box marginTop={4}>
          <Text
            id={descriptionId}
            color={theme.colors.content.tertiary}
            fontSize={14}
            fontWeight="400"
            style={
              orientation === "vertical" ? { textAlign: "center" } : undefined
            }
          >
            {description}
          </Text>
        </Box>
      )}
    </Box>
  );
};

ButtonGroup.displayName = "ButtonGroup";
